import { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface RegistrarProps {
  tempoPreenchido?: number;
  disciplinaId?: string;
  conteudoId?: string;
  tipoEstudo?: string;
}

export interface TimerProps {
  disciplinaId?: string;
  conteudoId?: string;
  tipoEstudo?: string;
}

interface ModalState {
  timerOpen: boolean;
  timerProps?: TimerProps;
  registrarOpen: boolean;
  registrarProps?: RegistrarProps;
}

interface ModalContextType extends ModalState {
  openTimer: (props?: TimerProps) => void;
  closeTimer: () => void;
  openRegistrar: (props?: RegistrarProps) => void;
  closeRegistrar: () => void;
}

const ModalContext = createContext<ModalContextType | null>(null);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ModalState>({
    timerOpen: false,
    registrarOpen: false,
  });

  const openTimer = useCallback((props?: TimerProps) =>
    setState(s => ({ ...s, timerOpen: true, timerProps: props })), []);
  const closeTimer = useCallback(() =>
    setState(s => ({ ...s, timerOpen: false, timerProps: undefined })), []);
  const openRegistrar = useCallback((props?: RegistrarProps) =>
    setState(s => ({ ...s, registrarOpen: true, registrarProps: props })), []);
  const closeRegistrar = useCallback(() =>
    setState(s => ({ ...s, registrarOpen: false, registrarProps: undefined })), []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only trigger if not in an input/textarea
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        if (e.key.toLowerCase() === 't') {
          e.preventDefault();
          openTimer();
        } else if (e.key.toLowerCase() === 'r') {
          e.preventDefault();
          openRegistrar();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [openTimer, openRegistrar]);

  return (
    <ModalContext.Provider value={{ ...state, openTimer, closeTimer, openRegistrar, closeRegistrar }}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModals() {
  const ctx = useContext(ModalContext);
  if (!ctx) throw new Error('useModals must be used within ModalProvider');
  return ctx;
}
